<script setup lang="ts">
/**
 * What the agent changed (or wants to change) in one turn, as a short recap rather than a list of
 * operations: one readable sentence ("Create a project, move 3 deliverables and 4 tasks, and update both
 * projects' rates"), the kinds of change as tiles, and every line behind "See all changes".
 *
 * Waiting: Not now / Approve. Applied: no card at all. Only the records the creator should review stay in the
 * conversation (a draft in full; an invoice, contract review or pitch as a chip); everything else it changed is one
 * quiet "N changes" receipt (ChangesPill) that opens the changes in the workspace pane, where each one, or all of
 * them, can be undone. Effects that leave Lighthouse (calendar invites) stay visible while it waits; email never
 * does: the agent drafts it, and the draft chip invites the creator to send it.
 *
 * A big change (more than 30 lines: an inbox cleanup of 4,000 threads) is never listed line by line: its digest
 * ("Archive 4,068 threads", "Move 397 threads to Trash", rewritten by a model when it can) stands in for the lines,
 * and once approved a progress card shows it leaving Lighthouse ("1,240 of 4,465 · about 2 min left"), then what
 * happened, including anything that couldn't be made and why (ChangesProgress). Small changes that take a while, or
 * that fail, get the same card.
 *
 * Phones (`chatBubbles`): a compact approval card that lines up with the agent's bubbles ("Needs your OK", the
 * recap in three lines, the kinds of change as one row of icons, full-width Not now | Approve). Tapping it opens
 * the ApprovalSheet with everything. Changes that send something out for good are approved with a press and
 * hold. Once applied the card morphs into the records to review (single drafts as mail cards, the rest in a
 * swipeable row) and the same "N changes" receipt, which opens the changes sheet.
 */
import { computed, inject, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue'
import type { ChangesetDigest } from '../api'
import type { UiChangeset } from '../reducer'
import type { AgentSession } from '../useAgentSession'
import { artifactsFromOps, artifactKey, changeRecords, needsReview, type ArtifactRef } from '../artifacts'
import { changeGroups, changesRecap, visibleSummary } from '../recap'
import { LEAVING_SHOWN, leavingEffects, needsHoldToApprove, phoneArtifactLayout } from '../cards'
import { BULK_LINES, MAILBOX_CHANGED_EVENT } from '../changesProgress'
import { useCardMode } from '../cardMode'
import { useDraftClaims } from '../draftClaims'
import { FRESH_MS, loadChangeset, loadRecap, peekChangeset, peekRecap, type ChangesetDetail } from '../recordCache'
import { haptic } from '@/native/haptics'
import { vPress } from '@/directives/press'
import ArtifactEntry from './ArtifactEntry.vue'
import ChangesPill from './ChangesPill.vue'
import ApprovalSheet from './ApprovalSheet.vue'
import ChangesProgress from './ChangesProgress.vue'
import HoldButton from './HoldButton.vue'

/** `order`: the card's place in the conversation, so a draft a later turn shows again gives way to it. */
const props = defineProps<{ changeset: UiChangeset; busy?: boolean; order?: number }>()
const emit = defineEmits<{ act: [action: 'approve' | 'reject'] }>()

const { phone: bubbles, inTranscript } = useCardMode()
const session = inject<AgentSession | null>('agentSession', null)

// Usually warmed with the conversation (recordCache.ts): then the card paints whole, with no skeleton.
const warm = peekChangeset(props.changeset.id, props.changeset.state)
const detail = ref<ChangesetDetail | null>(warm?.value ?? null)
const loaded = ref(Boolean(warm))
/** Painted whole on its first render (warmed): what the detail adds (records to review, the receipt) is simply there.
 *  Otherwise, or once the change moves on (approved here), that content animates in when it lands. */
const instant = ref(Boolean(warm))
const warmRecap = peekRecap(props.changeset.id)
/** The model-written recap, when the backend has one (fetched after the card is up, unless it was warmed). */
const aiRecap = ref<string | null>(warmRecap?.recap ?? null)
/** A big change's model-written digest (bullets), when the recap brought one. */
const aiDigest = ref<ChangesetDigest | null>(warmRecap?.digest?.lines?.length ? warmRecap.digest : null)
const expanded = ref(false)
const acting = ref<string | null>(null)

/** `maxAge`: what the cache got that recently is still good (the card mounting); otherwise always ask. */
async function loadDetail(maxAge = 0) {
    if (props.changeset.state === 'draft') return
    try {
        detail.value = await loadChangeset(props.changeset.id, props.changeset.state, maxAge)
    } catch {
        if (!loaded.value) detail.value = null
    } finally {
        loaded.value = true
    }
    if (!aiRecap.value) {
        loadRecap(props.changeset.id)
            .then((r) => {
                if (r?.recap) aiRecap.value = r.recap
                if (r?.digest?.lines?.length) aiDigest.value = r.digest
            })
            .catch(() => {})
    }
}
onMounted(() => loadDetail(FRESH_MS))
watch(() => props.changeset.state, (now, before) => {
    instant.value = false
    acting.value = null
    expanded.value = false
    if (bubbles.value) onPhoneStateChange(now, before)
    void loadDetail()
})

/** More lines than this are never put on the page at once, whatever the backend sent. */
const MAX_LINES = 100
const digest = computed(() => aiDigest.value ?? detail.value?.digest ?? null)
/** Too big to list: the digest stands in for the lines, and tiles (from a capped list of ops) would miscount. */
const bulk = computed(() => !!digest.value || (detail.value?.summary_total ?? props.changeset.summary.length) > BULK_LINES)
const allLines = computed(() => (digest.value ? digest.value.lines : visibleSummary(props.changeset.summary)))
const lines = computed(() => allLines.value.slice(0, MAX_LINES))
const hiddenLines = computed(() => allLines.value.length - lines.value.length)
/** How many changes it is (a digest's few lines stand for thousands). */
const total = computed(() => digest.value?.total ?? detail.value?.summary_total ?? allLines.value.length)
const groups = computed(() => (bulk.value ? [] : changeGroups(detail.value?.ops)))
const recap = computed(() => aiRecap.value || (bulk.value && digest.value?.recap) || changesRecap(lines.value, groups.value, aiRecap.value))
/** Tiles help when there's a mix; a single kind of change is already said by the recap. */
const tiles = computed(() => (groups.value.length > 1 || (groups.value[0]?.count ?? 0) > 1 ? groups.value : []))
const externalEffects = computed(() => leavingEffects(detail.value?.effects))
const shownEffects = computed(() => externalEffects.value.slice(0, LEAVING_SHOWN))
const moreEffects = computed(() => externalEffects.value.length - shownEffects.value.length)
const artifacts = computed<ArtifactRef[]>(() => (props.changeset.state === 'applied' ? artifactsFromOps(detail.value?.ops) : []))
/** Every record it wrote (the receipt's count); the ones put back one at a time from the changes pane. */
const records = computed(() => (['applied', 'undone'].includes(props.changeset.state) ? changeRecords(detail.value?.ops) : []))
/** The receipt's count: every record it wrote, even when the ops list stops short (a big change's stops at 1,000). */
const recordCount = computed(() => {
    const d = detail.value
    if (!d?.ops || d.ops.length >= d.ops_count) return records.value.length
    return Math.max(records.value.length, Object.values(d.affected ?? {}).reduce((a, b) => a + b, 0))
})
const undoneSeqs = computed(() => new Set(props.changeset.undone ?? detail.value?.undone ?? []))
const undoneKeys = computed(() => new Set(records.value
    .filter((r) => props.changeset.state === 'undone' || r.seqs.every((q) => undoneSeqs.value.has(q))).map((r) => r.key)))
/** What the creator should review, unless it was undone. */
const toReview = computed(() => artifacts.value.filter((a) => needsReview(a) && !undoneKeys.value.has(artifactKey(a))))
// A single draft it wrote claims this card's place; a later turn showing the same draft (revised) supersedes it.
const draftClaims = useDraftClaims()
const isSingleDraft = (a: ArtifactRef) => a.type === 'draft' && !a.items
watchEffect((onCleanup) => {
    const order = props.order
    if (!draftClaims || order === undefined) return
    const releases = toReview.value.filter(isSingleDraft).map((a) => draftClaims.claim(a.id, order))
    onCleanup(() => releases.forEach((r) => r()))
})
/** What stays in the conversation: what's left to review, less drafts that came back in a later turn. */
const reviewArtifacts = computed(() => toReview.value.filter((a) =>
    !(isSingleDraft(a) && draftClaims && props.order !== undefined && draftClaims.superseded(a.id, props.order))))

const state = computed(() => props.changeset.state)
/** A settled change has no card: applied or undone, its receipt (and what's left to review); declined, one quiet line. */
const chipOnly = computed(() => state.value === 'applied' || state.value === 'undone' || state.value === 'rejected')
const head = computed(() => {
    switch (state.value) {
        case 'pending_approval': return { text: 'Needs your OK', tone: 'wait' }
        case 'undone': return { text: 'Undone', tone: 'muted' }
        case 'rejected': return { text: 'Not applied', tone: 'muted' }
        default: return { text: 'Preview', tone: 'muted' }
    }
})

function act(action: 'approve' | 'reject') {
    acting.value = action
    emit('act', action)
    // The spinner stays until the change lands (the state watch) or fails (the session error); applying thousands
    // of records can take a while, and a button that comes back invites a second click.
    setTimeout(() => {
        if (acting.value !== action) return
        acting.value = null
    }, 120000)
}

/** The change has finished leaving Lighthouse: refresh what the card knows (statuses, whether undo still works). */
function onSettled() {
    if (detail.value?.effects?.some((e) => e.kind.startsWith('mail.')) && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(MAILBOX_CHANGED_EVENT, { detail: { changesetId: props.changeset.id } }))
    }
    void loadDetail()
}

// ── phones ───────────────────────────────────────────────────────────────────────────────────────────────────
const sheetOpen = ref(false)
/** Approving sends something out for good: press and hold. */
const hold = computed(() => needsHoldToApprove(props.changeset.maxRisk, detail.value?.effects))
const layout = computed(() => phoneArtifactLayout(reviewArtifacts.value))
/** The compact card shows while it waits, and for a beat after it's approved (it then folds into the records). */
const morph = ref<'idle' | 'approved' | 'folding'>('idle')
const showCard = computed(() => state.value === 'pending_approval' || morph.value !== 'idle')
const quiet = computed(() => state.value === 'rejected')
const changesLabel = computed(() => {
    const n = total.value
    return n ? `${n.toLocaleString('en-US')} change${n === 1 ? '' : 's'}` : ''
})
/** The kinds of change as one row of icons, one per kind of change (no labels: the sheet has them). */
const kinds = computed(() => groups.value.filter((g, i, all) => all.findIndex((x) => x.verb === g.verb) === i).slice(0, 5))
/** In a conversation the card opens the sheet; outside one (the call screen, over every sheet) it unfolds in place. */
function openDetails() {
    if (morph.value !== 'idle') return
    if (inTranscript) sheetOpen.value = true
    else expanded.value = !expanded.value
}
let timers: ReturnType<typeof setTimeout>[] = []
const later = (ms: number, fn: () => void) => timers.push(setTimeout(fn, ms))
onBeforeUnmount(() => timers.forEach(clearTimeout))

function onPhoneStateChange(now: string, before: string | undefined) {
    if (before !== 'pending_approval') return
    sheetOpen.value = false
    if (now === 'applied') {
        haptic('success')
        morph.value = 'approved'
        later(700, () => { morph.value = 'folding' })
        later(1120, () => { morph.value = 'idle' })
    } else if (now === 'rejected') {
        haptic('light')
    }
}

// A failed approve or reject: the session reports it; stop spinning (and on phones say so with a buzz).
watch(() => session?.error.value, (err) => {
    if (!err || !acting.value) return
    acting.value = null
    if (bubbles.value) haptic('error')
})

function approve(viaHold = false) {
    if (acting.value || props.busy) return
    // A completed hold already gave its commit tick.
    if (!viaHold) haptic('commit')
    act('approve')
}
function reject() {
    if (acting.value || props.busy) return
    act('reject')
}
</script>

<template>
    <!-- Phones: the compact approval card, then the records it touched. -->
    <div v-if="bubbles" class="cs is-bubble" :class="`is-${head.tone}`" :data-state="changeset.state" data-testid="changeset-card">
        <div v-if="showCard" class="agent-disclose pcs-fold" :class="{ 'is-open': morph !== 'folding' }">
            <div>
                <!-- The whole card opens the details for a finger; screen readers and keys use the Details button, so
                     Not now / Approve are never inside a role=button (its children would be hidden from VoiceOver). -->
                <div
                    v-press="{ variant: 'card', disabled: morph !== 'idle' }"
                    class="pcs"
                    :class="{ 'is-approved': morph !== 'idle' }"
                    data-testid="approval-card"
                    @click="openDetails"
                >
                    <header class="pcs-head">
                        <Transition name="agent-swap" mode="out-in">
                            <span v-if="morph === 'idle'" key="wait" class="pcs-badge">
                                <span class="pcs-dot" aria-hidden="true">!</span>Needs your OK
                            </span>
                            <span v-else key="done" class="pcs-badge is-done">
                                <span class="pcs-dot" aria-hidden="true">
                                    <svg viewBox="0 0 16 16"><path d="M4.2 8.4l2.4 2.3 5.2-5.4" /></svg>
                                </span>Approved
                            </span>
                        </Transition>
                        <button
                            v-if="morph === 'idle'"
                            type="button"
                            class="pcs-more"
                            :class="{ 'is-open': expanded }"
                            :aria-haspopup="inTranscript ? 'dialog' : undefined"
                            :aria-expanded="inTranscript ? undefined : expanded"
                            :aria-controls="inTranscript ? undefined : `pcs-lines-${changeset.id}`"
                            :aria-label="`Needs your OK: ${recap}. ${inTranscript ? 'Open details' : expanded ? 'Hide details' : 'Show details'}`"
                            data-testid="approval-details"
                            @click.stop="openDetails"
                        >
                            {{ inTranscript ? 'Details' : expanded ? 'Hide' : 'Details' }}
                            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6 4l4 4-4 4" /></svg>
                        </button>
                    </header>

                    <Transition name="agent-swap" mode="out-in">
                        <div v-if="!loaded" class="pcs-skel" aria-hidden="true">
                            <span class="loading-secondary h-4 w-11/12 rounded" />
                            <span class="loading-secondary h-4 w-3/5 rounded" />
                        </div>
                        <p v-else :key="recap" class="pcs-recap" data-testid="changeset-recap">{{ recap }}</p>
                    </Transition>

                    <div v-if="kinds.length || changesLabel" class="pcs-kinds">
                        <span v-for="g in kinds" :key="`${g.type}:${g.verb}`" class="pcs-kind" :class="`verb-${g.verb}`" :title="g.label">
                            <i class="fa-regular" :class="g.icon" aria-hidden="true" />
                        </span>
                        <span class="pcs-count">{{ changesLabel }}</span>
                    </div>

                    <p v-if="externalEffects.length" class="pcs-leaves">
                        <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6.5 4.5H11.5V9.5M11.5 4.5L4.5 11.5" /></svg>
                        <span class="pcs-leaves-text"><strong>Leaves Lighthouse</strong> · {{ externalEffects[0].summary }}<template v-if="externalEffects.length > 1"> +{{ externalEffects.length - 1 }}</template></span>
                    </p>

                    <div v-if="!inTranscript" :id="`pcs-lines-${changeset.id}`" class="agent-disclose" :class="{ 'is-open': expanded }">
                        <div>
                            <ul class="pcs-lines">
                                <li v-for="(l, i) in lines" :key="i">{{ l }}</li>
                                <li v-if="hiddenLines > 0" class="is-more">and {{ hiddenLines.toLocaleString('en-US') }} more</li>
                            </ul>
                            <p v-if="bulk" class="pcs-grouped">Grouped from {{ total.toLocaleString('en-US') }} changes</p>
                        </div>
                    </div>

                    <div class="pcs-actions" :class="{ 'is-gone': morph !== 'idle' }" @click.stop @pointerdown.stop>
                        <button
                            v-press.pill
                            type="button"
                            class="pcs-btn is-soft"
                            :disabled="busy || !!acting || morph !== 'idle'"
                            data-testid="reject"
                            @click="reject"
                        >
                            <span v-if="acting === 'reject'" class="pcs-spin is-dark" aria-hidden="true" />Not now
                        </button>
                        <HoldButton
                            v-if="hold"
                            class="pcs-btn"
                            :busy="acting === 'approve'"
                            :disabled="busy || acting === 'reject' || morph !== 'idle'"
                            @confirm="approve(true)"
                        />
                        <button
                            v-else
                            v-press.pill
                            type="button"
                            class="pcs-btn is-primary"
                            :disabled="busy || !!acting || morph !== 'idle'"
                            data-testid="approve"
                            @click="approve()"
                        >
                            <span v-if="acting === 'approve'" class="pcs-spin" aria-hidden="true" />Approve
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <p v-else-if="quiet" class="pcs-quiet" data-testid="changeset-quiet">
            <span class="pcs-quiet-mark" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="M5 5l6 6M11 5l-6 6" /></svg></span>
            <span class="min-w-0">
                <strong>{{ head.text }}</strong>
                <span class="pcs-quiet-recap"> · {{ recap }}</span>
            </span>
        </p>

        <template v-if="state === 'undone' && records.length">
            <ChangesPill :changeset-id="changeset.id" :count="records.length" :undone="records.length" bubble class="pcs-pill" />
            <p v-if="changeset.notUndone.length" class="pcs-quiet-note">A few things couldn't be undone: {{ changeset.notUndone.join('; ') }}</p>
        </template>
        <template v-if="state === 'applied'">
            <ChangesProgress
                v-if="detail"
                :changeset-id="changeset.id"
                :initial="detail.progress"
                :bulk="bulk"
                @settled="onSettled"
            />
            <TransitionGroup v-if="layout.lead.length" tag="div" name="agent-list" class="pcs-lead" :appear="!instant">
                <ArtifactEntry v-for="(a, i) in layout.lead" :key="artifactKey(a)" :artifact="a" :style="{ '--i': i }" />
            </TransitionGroup>
            <TransitionGroup
                v-if="layout.rest.length"
                tag="div"
                name="agent-list"
                :class="layout.carousel ? 'pcs-carousel' : 'pcs-single'"
                :role="layout.carousel ? 'list' : undefined"
                :aria-label="layout.carousel ? 'Records from this change' : undefined"
                data-testid="artifact-row"
                :appear="!instant"
            >
                <div v-for="(a, i) in layout.rest" :key="artifactKey(a)" class="pcs-slide" :role="layout.carousel ? 'listitem' : undefined" :style="{ '--i': i + layout.lead.length }">
                    <ArtifactEntry :artifact="a" />
                </div>
            </TransitionGroup>
            <Transition name="agent-list" :appear="!instant">
                <ChangesPill v-if="records.length" :changeset-id="changeset.id" :count="recordCount" :undone="undoneKeys.size" bubble class="pcs-pill" />
            </Transition>
        </template>

        <ApprovalSheet
            v-if="inTranscript"
            :open="sheetOpen && state === 'pending_approval'"
            :recap="recap"
            :lines="lines"
            :groups="groups"
            :leaving="externalEffects.slice(0, 10)"
            :reasons="changeset.reasons"
            :hold="hold"
            :acting="(acting as 'approve' | 'reject' | null)"
            :disabled="busy"
            @close="sheetOpen = false"
            @approve="approve(hold)"
            @reject="reject"
        />
    </div>

    <div v-else class="cs" :class="`is-${head.tone}`" :data-state="changeset.state" data-testid="changeset-card">
        <div v-if="!chipOnly" class="cs-card">
            <header class="cs-head">
                <Transition name="agent-swap" mode="out-in">
                    <span :key="state" class="badge">
                        <span class="badge-mark" aria-hidden="true">
                            <svg v-if="state === 'undone'" viewBox="0 0 16 16"><path d="M5 6.5h5.2a2.8 2.8 0 010 5.6H7M5 6.5L7 4.5M5 6.5l2 2" /></svg>
                            <svg v-else-if="state === 'pending_approval'" viewBox="0 0 16 16"><path d="M8 4.8v3.6M8 11.1v.1" /></svg>
                            <svg v-else viewBox="0 0 16 16"><path d="M5.5 8h5" /></svg>
                        </span>
                        {{ head.text }}
                    </span>
                </Transition>
            </header>

            <!-- The recap: one sentence that says what this does. -->
            <div v-if="!loaded" class="recap-skel" aria-hidden="true">
                <span class="loading-secondary h-3.5 w-11/12 rounded" />
                <span class="loading-secondary h-3.5 w-3/5 rounded" />
            </div>
            <Transition v-else name="agent-swap" mode="out-in">
                <p :key="recap" class="recap" :class="{ 'is-struck': state === 'undone' || state === 'rejected' }" data-testid="changeset-recap">{{ recap }}</p>
            </Transition>

            <!-- What kinds of change, at a glance. -->
            <TransitionGroup v-if="tiles.length" tag="ul" name="agent-list" class="tiles" :appear="!instant">
                <li v-for="(g, i) in tiles" :key="`${g.type}:${g.verb}`" class="tile" :class="`verb-${g.verb}`" :style="{ '--i': i }">
                    <span class="tile-icon" aria-hidden="true"><i class="fa-regular" :class="g.icon" /></span>
                    <span class="tile-text">{{ g.label }}</span>
                </li>
            </TransitionGroup>

            <p v-if="externalEffects.length && state === 'pending_approval'" class="leaves">
                <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6.5 4.5H11.5V9.5M11.5 4.5L4.5 11.5" /></svg>
                <span>{{ shownEffects.map((e) => e.summary).join(' · ') }}<template v-if="moreEffects > 0"> · +{{ moreEffects.toLocaleString('en-US') }} more</template></span>
            </p>
            <p v-if="state === 'undone' && changeset.notUndone.length" class="note">
                A few things couldn't be undone: {{ changeset.notUndone.join('; ') }}
            </p>

            <footer class="cs-foot">
                <button v-if="lines.length" type="button" class="see-all" :aria-expanded="expanded" @click="expanded = !expanded">
                    <svg viewBox="0 0 16 16" :class="{ 'is-open': expanded }" aria-hidden="true"><path d="M6 4l4 4-4 4" /></svg>
                    {{ expanded ? 'Hide details' : bulk ? 'Show details' : `See all ${lines.length} change${lines.length === 1 ? '' : 's'}` }}
                </button>
                <div v-if="state === 'pending_approval'" class="actions">
                    <button type="button" class="agent-btn is-soft" :disabled="busy || !!acting" data-testid="reject" @click="act('reject')">Not now</button>
                    <button type="button" class="agent-btn is-primary" :disabled="busy || !!acting" data-testid="approve" @click="act('approve')">
                        <span v-if="acting === 'approve'" class="btn-spin" aria-hidden="true" />{{ acting === 'approve' ? 'Approving…' : 'Approve' }}
                    </button>
                </div>
            </footer>

            <div class="agent-disclose" :class="{ 'is-open': expanded }">
                <div>
                    <ul class="lines" :class="{ 'is-struck': state === 'undone' || state === 'rejected' }" data-testid="changeset-lines">
                        <li v-for="(l, i) in lines" :key="i">{{ l }}</li>
                        <li v-if="hiddenLines > 0" class="is-more">and {{ hiddenLines.toLocaleString('en-US') }} more</li>
                    </ul>
                    <p v-if="bulk" class="grouped">Grouped from {{ total.toLocaleString('en-US') }} changes</p>
                </div>
            </div>
        </div>

        <ChangesProgress
            v-if="state === 'applied' && detail"
            :changeset-id="changeset.id"
            :initial="detail.progress"
            :bulk="bulk"
            @settled="onSettled"
        />
        <TransitionGroup v-if="reviewArtifacts.length" tag="div" name="agent-list" class="artifacts" :appear="!instant">
            <ArtifactEntry v-for="(a, i) in reviewArtifacts" :key="artifactKey(a)" :artifact="a" :style="{ '--i': i }" />
        </TransitionGroup>
        <Transition name="agent-list" :appear="!instant">
            <ChangesPill v-if="(state === 'applied' || state === 'undone') && records.length" :changeset-id="changeset.id" :count="recordCount" :undone="undoneKeys.size" />
        </Transition>
        <p v-if="state === 'undone' && changeset.notUndone.length" class="pcs-quiet-note">A few things couldn't be undone: {{ changeset.notUndone.join('; ') }}</p>
        <p v-if="state === 'rejected'" class="pcs-quiet" data-testid="changeset-quiet">
            <span class="pcs-quiet-mark" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="M5 5l6 6M11 5l-6 6" /></svg></span>
            <span class="min-w-0"><strong>{{ head.text }}</strong><span class="pcs-quiet-recap"> · {{ recap }}</span></span>
        </p>
    </div>
</template>

<style scoped>
.cs { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.cs-card {
    padding: 14px 16px 12px;
    border-radius: var(--agent-radius-md);
    background: var(--agent-card);
    border: 1px solid var(--agent-card-border);
    box-shadow: var(--agent-card-shadow);
    transition: border-color 300ms ease, box-shadow 300ms ease;
}
.is-wait .cs-card {
    border-color: rgb(251 76 31 / 0.22);
    box-shadow: 0 0 0 4px rgb(251 76 31 / 0.05), var(--agent-card-shadow);
}
.is-muted .cs-card { box-shadow: none; background: transparent; }

.cs-head { display: flex; align-items: center; gap: 8px; min-height: 26px; }
.badge { display: inline-flex; align-items: center; gap: 7px; font-size: 12.5px; font-weight: 600; color: var(--agent-text-2); }
.badge-mark { display: inline-flex; width: 18px; height: 18px; border-radius: 999px; animation: agent-pop 420ms var(--agent-spring) both; }
.badge-mark svg { width: 18px; height: 18px; fill: none; stroke: #fff; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
.is-done .badge-mark { background: #10b981; }
.is-wait .badge-mark { background: var(--agent-accent); }
.is-wait .badge { color: #e2400f; }
.is-muted .badge-mark { background: var(--agent-text-4); }
.is-muted .badge { color: var(--agent-text-3); }
.btn-spin, .see-all svg, .leaves svg {
    width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 1.6; stroke-linecap: round; stroke-linejoin: round;
}
.spinning { animation: spin-back 0.9s linear infinite; }
@keyframes spin-back { to { transform: rotate(-360deg); } }

.recap {
    margin-top: 8px;
    font-size: 15px;
    line-height: 1.55;
    color: var(--agent-text-1);
    text-wrap: pretty;
}
.recap.is-struck { color: var(--agent-text-3); text-decoration: line-through; text-decoration-color: rgb(15 15 15 / 0.2); }
.recap-skel { display: flex; flex-direction: column; gap: 8px; margin-top: 12px; }

.tiles { display: flex; flex-wrap: wrap; gap: 6px; margin: 12px 0 0; padding: 0; list-style: none; }
.tile {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 32px;
    padding: 4px 11px 4px 4px;
    border-radius: 999px;
    font-size: 12.5px;
    color: var(--agent-text-2);
    background: var(--agent-hover);
}
.tile-icon {
    display: flex; align-items: center; justify-content: center;
    width: 24px; height: 24px; border-radius: 999px; font-size: 11px;
    color: var(--agent-text-2); background: var(--agent-card);
    box-shadow: 0 1px 2px rgb(15 15 15 / 0.08);
}
.verb-create .tile-icon { color: #059669; }
.verb-move .tile-icon { color: #2563eb; }
.verb-rates .tile-icon { color: #059669; }
.verb-status .tile-icon { color: #d97706; }
.verb-dates .tile-icon { color: #7c3aed; }
.verb-remove .tile-icon { color: #dc2626; }
.verb-send .tile-icon { color: #e2400f; }
.is-muted .tile { opacity: 0.6; }

.leaves {
    display: flex; align-items: flex-start; gap: 6px;
    margin-top: 10px; font-size: 12.5px; line-height: 18px; color: var(--agent-text-3);
}
.leaves svg { flex-shrink: 0; margin-top: 2px; }
.note { margin-top: 10px; font-size: 12.5px; color: var(--agent-text-3); }

.cs-foot { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 12px; }
.see-all {
    display: inline-flex; align-items: center; gap: 5px;
    min-height: 30px; margin-left: -6px; padding: 0 8px; border-radius: 10px;
    font-size: 12.5px; color: var(--agent-text-3);
}
@media (hover: hover) and (pointer: fine) {
    .see-all:hover { background: var(--agent-hover); color: var(--agent-text-1); }
}
.see-all svg { width: 12px; height: 12px; transition: transform 300ms var(--agent-spring); }
.see-all svg.is-open { transform: rotate(90deg); }
.actions { display: flex; gap: 6px; margin-left: auto; }
.btn-spin {
    display: inline-block; border-radius: 999px;
    border: 1.6px solid rgb(255 255 255 / 0.4); border-top-color: #fff;
    animation: spin-back 0.7s linear infinite reverse;
}

.lines {
    display: flex; flex-direction: column; gap: 3px;
    margin: 6px 0 2px; padding: 10px 12px; list-style: none;
    border-radius: 12px; background: var(--agent-hover);
    font-size: 12.5px; line-height: 18px; color: var(--agent-text-2);
}
.lines li { position: relative; padding-left: 13px; overflow-wrap: anywhere; }
.lines li::before {
    content: ''; position: absolute; left: 2px; top: 7px;
    width: 4px; height: 4px; border-radius: 999px; background: var(--agent-text-4);
}
.lines.is-struck { color: var(--agent-text-4); text-decoration: line-through; }
.lines li.is-more::before, .pcs-lines li.is-more::before { display: none; }
.lines li.is-more, .pcs-lines li.is-more { padding-left: 0; color: var(--agent-text-3); }
.grouped, .pcs-grouped { margin: 4px 2px 0; font-size: 12px; color: var(--agent-text-4); }

.artifacts { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 8px; }

/* ── Phones ─────────────────────────────────────────────────────────────────────────────────────────────── */
.cs.is-bubble { gap: 8px; }
.pcs-fold { margin: -6px; transition: grid-template-rows 420ms var(--lh-ease-ios, ease), opacity 300ms ease; }
/* Room for the card's ring and shadow inside the fold's clip. */
.pcs-fold > div { padding: 6px; }
.pcs {
    position: relative;
    padding: 14px 14px 12px;
    border-radius: 22px;
    background: var(--agent-card);
    box-shadow:
        0 0 0 1px rgb(251 76 31 / 0.2),
        0 0 0 5px rgb(251 76 31 / 0.05),
        0 8px 22px -12px rgb(15 15 15 / 0.22);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    transition: box-shadow 360ms var(--lh-ease-out, ease), transform 220ms var(--lh-spring-soft, ease);
    outline: none;
}
.pcs:focus-visible { box-shadow: 0 0 0 2px var(--agent-accent-ring), 0 8px 22px -12px rgb(15 15 15 / 0.22); }
.pcs.is-approved {
    cursor: default;
    box-shadow: 0 0 0 1px rgb(16 185 129 / 0.3), 0 0 0 5px rgb(16 185 129 / 0.07), 0 8px 22px -12px rgb(15 15 15 / 0.22);
}
html.dark .pcs { box-shadow: 0 0 0 1px rgb(251 76 31 / 0.32), 0 0 0 5px rgb(251 76 31 / 0.07), 0 10px 26px -12px rgb(0 0 0 / 0.7); }
html.dark .pcs.is-approved { box-shadow: 0 0 0 1px rgb(16 185 129 / 0.4), 0 0 0 5px rgb(16 185 129 / 0.1), 0 10px 26px -12px rgb(0 0 0 / 0.7); }

.pcs-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; min-height: 24px; }
.pcs-badge {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 13.5px;
    font-weight: 650;
    color: #e2400f;
}
html.dark .pcs-badge { color: #ff8a5c; }
.pcs-badge.is-done { color: #059669; }
html.dark .pcs-badge.is-done { color: #34d399; }
.pcs-dot {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    color: #fff;
    background: var(--agent-accent);
    animation: agent-pop 420ms var(--lh-spring-pop, ease) both;
}
.is-done .pcs-dot { background: #10b981; }
.pcs-dot svg { width: 14px; height: 14px; fill: none; stroke: #fff; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.pcs-more {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-size: 13px;
    color: var(--agent-text-3);
    border-radius: 8px;
}
/* A 44px-tall hit area around the small label. */
.pcs-more::before { content: ''; position: absolute; inset: -10px -12px; }
.pcs-more:focus-visible { outline: 2px solid var(--agent-accent-ring, #fb4c1f); outline-offset: 3px; }
.pcs-more svg { width: 13px; height: 13px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; transition: transform 300ms var(--lh-spring-soft, ease); }
.pcs-more.is-open svg { transform: rotate(90deg); }
.pcs-lines {
    display: flex; flex-direction: column; gap: 4px;
    margin: 10px 0 0; padding: 10px 12px; list-style: none;
    border-radius: 12px; background: var(--agent-hover);
    font-size: 13.5px; line-height: 1.4; color: var(--agent-text-2);
}
.pcs-lines li { position: relative; padding-left: 13px; overflow-wrap: anywhere; }
.pcs-lines li::before { content: ''; position: absolute; left: 2px; top: 8px; width: 4px; height: 4px; border-radius: 999px; background: var(--agent-text-4); }

.pcs-recap {
    display: -webkit-box;
    margin-top: 8px;
    overflow: hidden;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    font-size: 16px;
    line-height: 1.4;
    color: var(--agent-text-1);
    text-wrap: pretty;
}
.pcs-skel { display: flex; flex-direction: column; gap: 8px; margin-top: 12px; }

.pcs-kinds { display: flex; align-items: center; margin-top: 10px; }
.pcs-kind {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 999px;
    font-size: 11.5px;
    color: var(--agent-text-2);
    background: var(--agent-card);
    box-shadow: 0 0 0 2px var(--agent-card), inset 0 0 0 1px var(--agent-card-border);
}
.pcs-kind + .pcs-kind { margin-left: -4px; }
.pcs-kind.verb-create, .pcs-kind.verb-rates { color: #059669; background: rgb(16 185 129 / 0.1); }
.pcs-kind.verb-move { color: #2563eb; background: rgb(59 130 246 / 0.1); }
.pcs-kind.verb-status { color: #d97706; background: rgb(245 158 11 / 0.12); }
.pcs-kind.verb-dates { color: #7c3aed; background: rgb(139 92 246 / 0.11); }
.pcs-kind.verb-remove { color: #dc2626; background: rgb(239 68 68 / 0.1); }
.pcs-kind.verb-send { color: #e2400f; background: rgb(251 76 31 / 0.1); }
.pcs-count { margin-left: 10px; font-size: 13px; color: var(--agent-text-3); }

.pcs-leaves {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 10px;
    padding: 8px 10px;
    border-radius: 12px;
    font-size: 13px;
    line-height: 1.35;
    color: var(--agent-text-2);
    background: rgb(251 76 31 / 0.07);
}
html.dark .pcs-leaves { background: rgb(251 76 31 / 0.13); }
.pcs-leaves svg {
    flex-shrink: 0; width: 14px; height: 14px; margin-top: 2px;
    fill: none; stroke: #e2400f; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round;
}
.pcs-leaves-text { display: -webkit-box; overflow: hidden; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
.pcs-leaves strong { font-weight: 600; color: #e2400f; }
html.dark .pcs-leaves strong { color: #ff8a5c; }

.pcs-actions {
    display: flex;
    gap: 8px;
    margin-top: 14px;
    transition: opacity 240ms ease, transform 300ms var(--lh-ease-out, ease);
}
.pcs-actions.is-gone { opacity: 0; transform: translateY(4px); pointer-events: none; }
.pcs-btn {
    display: inline-flex;
    flex: 1 1 0;
    min-width: 0;
    white-space: nowrap;
    align-items: center;
    justify-content: center;
    gap: 7px;
    min-height: 44px;
    border-radius: 14px;
    font-size: 15px;
    font-weight: 600;
    -webkit-tap-highlight-color: transparent;
}
.pcs-btn:disabled { opacity: 0.5; }
/* "Hold to approve" needs a little more room than Not now. */
.pcs-btn.hold { flex-grow: 1.45; padding: 0 12px; }
.pcs-btn.is-soft { color: var(--agent-text-1); background: var(--agent-press); }
.pcs-btn.is-primary {
    color: #fff;
    background: linear-gradient(180deg, var(--agent-accent-2) 0%, var(--agent-accent) 100%);
    box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.35), 0 6px 16px -6px rgb(251 76 31 / 0.6);
}
.pcs-btn.is-primary:disabled { opacity: 1; }
.pcs-spin {
    flex-shrink: 0;
    width: 15px;
    height: 15px;
    border-radius: 999px;
    border: 2px solid rgb(255 255 255 / 0.4);
    border-top-color: #fff;
    animation: spin-back 0.7s linear infinite reverse;
}
.pcs-spin.is-dark { border-color: rgb(15 15 15 / 0.15); border-top-color: var(--agent-text-2); }

.pcs-quiet {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 2px 4px;
    font-size: 13px;
    line-height: 1.4;
    color: var(--agent-text-3);
}
.pcs-quiet strong { font-weight: 600; color: var(--agent-text-2); }
.pcs-quiet-recap { overflow-wrap: anywhere; }
.pcs-quiet-note { display: block; margin-top: 2px; color: var(--agent-text-4); }
.pcs-quiet-mark {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    margin-top: 0.5px;
    border-radius: 999px;
    background: var(--agent-press);
}
.pcs-quiet-mark svg { width: 12px; height: 12px; fill: none; stroke: var(--agent-text-3); stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }

.pcs-lead { display: flex; flex-direction: column; gap: 8px; }
.pcs-single { display: flex; flex-direction: column; }
/* Several records: a swipeable row that bleeds to the screen edges, the next card peeking. */
.pcs-carousel {
    display: flex;
    gap: 8px;
    /* --chat-card-bleed-end: how far the phone chat's card column stops short of the screen's right edge. */
    margin: -6px calc(-16px - var(--chat-card-bleed-end, 0px)) -12px -16px;
    padding: 6px calc(16px + var(--chat-card-bleed-end, 0px)) 18px 16px;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    scroll-padding-inline: 16px;
    overscroll-behavior-x: contain;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
}
.pcs-carousel::-webkit-scrollbar { display: none; }
.pcs-carousel .pcs-slide {
    display: flex;
    flex: 0 0 85%;
    min-width: 0;
    scroll-snap-align: start;
}
.pcs-carousel .pcs-slide > * { flex: 1; }

.pcs-pill { margin-left: 2px; }

@media (prefers-reduced-motion: reduce) {
    .pcs-fold { transition: opacity 160ms ease; }
}

/* On the voice call screen (always dark glass, whatever the app theme): the call's card slot sets the dark
   glass tokens; these are the hues that aren't tokens. */
.vc-card .pcs { box-shadow: inset 0 0 0 1px rgb(255 140 92 / 0.35); }
.vc-card .pcs-badge { color: #ff9a70; }
.vc-card .pcs-leaves { background: rgb(251 76 31 / 0.16); }
.vc-card .pcs-leaves strong { color: #ff9a70; }
.vc-card .pcs-kind { box-shadow: 0 0 0 2px rgb(40 32 30); }
</style>
